import React, { useContext, useMemo, useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Button,
  Card,
  CardContent,
  Container,
  Link,
  Grid,
  TextField,
  Typography,
  Box,
  Stack,
  Switch,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

// API + helpers
import { forgetPassword } from "../../services/api/user-service";
import { useConfirm } from "@/components/dialog/confirm";
import EnvironmentFlag from "@/components/environmentFlag";
import type { ForgotPasswordRequest } from "@/services/model/forgetPasswordRequest";
import BrandLoader from "@/branding/BrandLoader";
import BrandLogo from "@/branding/BrandLogo";
import { BrandingContext } from "@/branding/BrandingProvider";

type ForgotPasswordProps = {
  style?: any;
  loading: boolean;
};

const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const numberRegex = /^(\+\d{1,4}[- ]?)?\d{7,}$/;

const ForgotPassword: React.FC<ForgotPasswordProps> = (_props) => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const [loading, setLoading] = useState(false);
  const branding = useContext(BrandingContext);

  const smsAllowed = !!branding?.enableSMS; // <-- your flag

  const goToLogin = () => navigate(tenant ? `/${tenant}/login` : "/login");

  const showDialog = (message: string, success: boolean) => {
    const title = success ? "Password Reset" : "Password Reset Failed";
    confirm({ catchOnCancel: false, title, description: message }).then(() => {
      if (success) goToLogin();
    });
  };

  // Conditional schema based on notificationType (false=email, true=sms)
  // and whether the brand allows SMS
  const schema = useMemo(
    () =>
      Yup.object({
        notificationType: Yup.boolean()
          .default(false)
          .test(
            "sms-allowed",
            "SMS reset is not available for this brand.",
            (val) => (smsAllowed ? true : val === false)
          ),
        email: Yup.string()
          .matches(emailRegex, "Email address is not valid")
          .when("notificationType", {
            is: false,
            then: (s) => s.required("Email address is required"),
            otherwise: (s) => s.notRequired(),
          }),
        mobileNumber: Yup.string()
          .matches(numberRegex, "Mobile number is not valid")
          .when("notificationType", {
            is: true,
            then: (s) =>
              smsAllowed
                ? s
                    .min(10, "Phone number is invalid")
                    .required("Phone number is required")
                : s.notRequired(), // if brand doesn't allow sms, never require it
            otherwise: (s) => s.notRequired(),
          }),
      }),
    [smsAllowed]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={{
        notificationType: false, // always start on email
        email: "",
        mobileNumber: "",
      }}
      validationSchema={schema}
      onSubmit={async (fields) => {
        try {
          setLoading(true);

          const payload: ForgotPasswordRequest = {
            email: fields.email,
            mobileNumber: fields.mobileNumber,
            isApp: true,
            appTenantName: tenant,
          };

          const response = await forgetPassword(payload);
          if (response) {
            let message = response.message;
            if (response.resultCode) {
              message =
                smsAllowed && fields.notificationType
                  ? "If an account with that mobile number exists, we've sent password reset instructions via SMS."
                  : "If an account with that email exists, we've sent password reset instructions.";
            }
            showDialog(message, response.resultCode);
          } else {
            showDialog(
              "There seems to be a problem with your internet connection. Please check your connection and try again.",
              false
            );
          }
        } catch (err) {
          showDialog(
            "An exception occurred, please contact your administrator.",
            false
          );
          if (err instanceof Error) console.error(err.message);
        } finally {
          setLoading(false);
        }
      }}
    >
      {(formik) => {
        const {
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          setFieldValue,
        } = formik;

        // If branding changes and SMS becomes disallowed, force email mode.
        useEffect(() => {
          if (!smsAllowed && values.notificationType) {
            setFieldValue("notificationType", false);
            setFieldValue("mobileNumber", "");
          }
        }, [smsAllowed, values.notificationType, setFieldValue]);

        const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (!smsAllowed) return; // hard guard
          const next = e.target.checked;
          setFieldValue("notificationType", next);
          if (next) setFieldValue("email", "");
          else setFieldValue("mobileNumber", "");
        };

        return (
          <>
            <Container maxWidth="sm">
              <Grid
                container
                spacing={2}
                justifyContent="center"
                direction="column"
              >
                <Card variant="outlined">
                  <CardContent>
                    <EnvironmentFlag />
                    <Grid container justifyContent="center">
                      <BrandLogo size={150} shape="circle" ring shadow />
                    </Grid>

                    <Box sx={{ height: 60 }} />

                    <Grid size={12}>
                      <Grid container justifyContent="center">
                        <Typography align="center" variant="subtitle1">
                          {smsAllowed && values.notificationType
                            ? "Enter your mobile number and we'll send you instructions to reset your password."
                            : "Enter your email address and we'll send you instructions to reset your password."}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid size={12} sx={{ mt: 2 }}>
                      <Form noValidate>
                        <Grid container spacing={2}>
                          {smsAllowed && ( // <-- ONLY show switch if allowed
                            <Grid size={12}>
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Typography>Email</Typography>
                                <Switch
                                  checked={values.notificationType}
                                  onChange={handleSwitchChange}
                                />
                                <Typography>SMS</Typography>
                              </Stack>
                            </Grid>
                          )}

                          {!values.notificationType && (
                            <Grid size={12}>
                              <TextField
                                type="text"
                                label="Email"
                                name="email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                variant="outlined"
                                required
                                error={!!(errors.email && touched.email)}
                                helperText={touched.email && errors.email}
                              />
                            </Grid>
                          )}

                          {smsAllowed && values.notificationType && (
                            <Grid size={12}>
                              <TextField
                                type="text"
                                label="Mobile Number"
                                name="mobileNumber"
                                placeholder="Eg. +2712 3456789; +27 123456789; 0123456789"
                                value={values.mobileNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                variant="outlined"
                                required
                                error={
                                  !!(
                                    errors.mobileNumber && touched.mobileNumber
                                  )
                                }
                                helperText={
                                  touched.mobileNumber && errors.mobileNumber
                                }
                              />
                            </Grid>
                          )}

                          <Grid size={12} sx={{ mt: 1 }}>
                            <Button
                              type="submit"
                              variant="contained"
                              color="secondary"
                              fullWidth
                              size="large"
                              disabled={loading}
                            >
                              <strong>Next</strong>
                            </Button>
                          </Grid>
                        </Grid>
                      </Form>
                    </Grid>

                    <Grid size={12} sx={{ mt: 2 }}>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        direction="column"
                      >
                        <Typography>
                          <Link
                            component="button"
                            onClick={goToLogin}
                            underline="always"
                            color="secondary"
                          >
                            <strong>Go back to Login - Click Here</strong>
                          </Link>
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Container>

            <BrandLoader open={loading} />
          </>
        );
      }}
    </Formik>
  );
};

export default ForgotPassword;
